import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { TtsFlowComposeWizard } from '../components/TtsFlowComposeWizard';
import { useTtsFlowDetail, useUpdateTtsFlow } from '../api';
import {
  buildTtsFlowUpdateRequest,
  hydrateTtsFlowFromDetail,
} from '../payload';
import { synthesizePendingTextRows } from '../synthesize';
import {
  DEFAULT_TTS_FORM_VALUES,
  type TtsFlowFormValues,
} from '../types';

export function UpdateTtsFlowPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const { ttsFlowId } = useParams<{ ttsFlowId: string }>();
  const id = Number(ttsFlowId);

  const detail = useTtsFlowDetail(id);
  const update = useUpdateTtsFlow();
  const [synthesizing, setSynthesizing] = useState(false);

  const initial = useMemo<TtsFlowFormValues | null>(() => {
    if (!detail.data) return null;
    return {
      ...DEFAULT_TTS_FORM_VALUES,
      ...hydrateTtsFlowFromDetail(detail.data),
    };
  }, [detail.data]);

  if (detail.isLoading || !initial) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading TTS flow…
      </div>
    );
  }

  const handleSubmit = async (values: TtsFlowFormValues) => {
    setSynthesizing(true);
    try {
      const rows = await synthesizePendingTextRows({
        userId: Number(userId),
        values,
      });
      const finalValues = { ...values, ttsRows: rows };
      update.mutate(
        buildTtsFlowUpdateRequest({
          userId: Number(userId),
          values: finalValues,
          id,
        }),
        {
          onSuccess: (res) => {
            if (res?.message === 'Parameters Incorrect') {
              toast.error(res.message);
              return;
            }
            toast.success(res?.message ?? 'TTS flow updated');
            navigate('/tts-flow-list');
          },
          onError: () => toast.error('Could not update the TTS flow.'),
        },
      );
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Could not synthesize inline text rows.',
      );
    } finally {
      setSynthesizing(false);
    }
  };

  return (
    <TtsFlowComposeWizard
      mode="update"
      initialValues={initial}
      isPending={update.isPending || synthesizing}
      onSubmit={handleSubmit}
    />
  );
}
